// Strip location and camera metadata from an image before it is uploaded.
//
// This is not a theoretical risk. A live photo on a paying customer's blog
// carries `GPS 40.351701, 18.166778` plus the camera body and the exact
// timestamp, readable by anyone who downloads it — and a travel blogger's
// camera roll contains their home as often as it contains a piazza.
//
// Only future uploads are cleaned (JP, 2026-07-25). Rewriting what is already
// in the archive would change URLs that are already indexed and linked.
//
// The rewrite is LOSSLESS: it removes metadata segments from the container and
// leaves the compressed image data untouched. Re-encoding through a canvas
// would also drop the metadata, but it would re-compress a photo the author
// chose to upload at full quality.

// APP segments to drop from a JPEG.
//   APP1  (0xE1) — EXIF, which is where GPS lives, and XMP
//   APP13 (0xED) — Photoshop/IPTC, which carries its own location fields
// APP0 (JFIF) is kept because some decoders expect it, and APP2 is kept
// because that is the ICC colour profile — dropping it visibly shifts colour.
const JPEG_DROP_MARKERS = new Set([0xe1, 0xed]);

// Orientation (EXIF tag 0x0112) says which way up the photo is. It lives in
// APP1 with the GPS, so dropping the segment dropped it too — and nothing
// downstream re-applies rotation: the upload helper resizes with blueimp but
// never passes `orientation`, and imgproxy can only auto-rotate from EXIF that
// still exists. A portrait phone photo therefore published on its side.
//
// So: read the tag out before the segment goes, and write a minimal APP1 back
// carrying ONLY that tag. Still lossless — no pixel is re-encoded — and the
// rebuilt segment holds one integer, so there is nothing in it to leak.
const EXIF_ORIENTATION_TAG = 0x0112;

const readOrientation = bytes => {
  // "Exif\0\0" then a TIFF header, whose byte order its first two bytes declare.
  const tiff = 10;
  if (bytes.length < tiff + 8) return 0;
  const le = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
  const be = bytes[tiff] === 0x4d && bytes[tiff + 1] === 0x4d;
  if (!le && !be) return 0;
  const u16 = at =>
    le ? bytes[at] | (bytes[at + 1] << 8) : (bytes[at] << 8) | bytes[at + 1];
  const u32 = at =>
    le
      ? (bytes[at] |
          (bytes[at + 1] << 8) |
          (bytes[at + 2] << 16) |
          (bytes[at + 3] << 24)) >>> 0
      : ((bytes[at] << 24) |
          (bytes[at + 1] << 16) |
          (bytes[at + 2] << 8) |
          bytes[at + 3]) >>> 0;
  if (u16(tiff + 2) !== 0x002a) return 0;
  const ifd0 = tiff + u32(tiff + 4);
  if (ifd0 + 2 > bytes.length) return 0;
  const entries = u16(ifd0);
  for (let i = 0; i < entries; i += 1) {
    const entry = ifd0 + 2 + i * 12;
    if (entry + 12 > bytes.length) break;
    if (u16(entry) === EXIF_ORIENTATION_TAG) {
      // SHORT, count 1: the value sits in the first two bytes of the value
      // field, whichever way round the file is.
      const value = u16(entry + 8);
      return value >= 1 && value <= 8 ? value : 0;
    }
  }
  return 0;
};

// A whole APP1/EXIF segment holding a single big-endian Orientation entry.
const orientationSegment = orientation =>
  Uint8Array.from([
    0xff, 0xe1, 0x00, 0x22, // APP1, length 34 (the 2 length bytes + 32 payload)
    0x45, 0x78, 0x69, 0x66, 0x00, 0x00, // "Exif\0\0"
    0x4d, 0x4d, 0x00, 0x2a, // big-endian TIFF header
    0x00, 0x00, 0x00, 0x08, // IFD0 begins 8 bytes in
    0x00, 0x01, // one entry
    (EXIF_ORIENTATION_TAG >> 8) & 0xff, EXIF_ORIENTATION_TAG & 0xff,
    0x00, 0x03, // type SHORT
    0x00, 0x00, 0x00, 0x01, // count 1
    (orientation >> 8) & 0xff, orientation & 0xff, 0x00, 0x00, // value
    0x00, 0x00, 0x00, 0x00, // no next IFD
  ]);

const stripJpeg = buffer => {
  const bytes = new Uint8Array(buffer);
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null; // not a JPEG

  const keep = [[0, 2]];
  let offset = 2;
  // Only a run that reaches the scan is trustworthy. Anything else means we
  // stopped understanding the file partway through, and emitting the ranges
  // parsed so far would upload a TRUNCATED image — worse than not stripping.
  let reachedScan = false;
  // Captured from the EXIF before it is dropped, and written back on its own.
  let orientation = 0;

  while (offset < bytes.length - 1) {
    if (bytes[offset] !== 0xff) break; // desynchronised
    let marker = bytes[offset + 1];

    // 0xFF is legal marker padding; skip the fill bytes and re-read.
    if (marker === 0xff) {
      let probe = offset + 1;
      while (probe < bytes.length && bytes[probe] === 0xff) probe += 1;
      if (probe >= bytes.length) break;
      keep.push([offset, probe]);
      offset = probe - 1;
      marker = bytes[offset + 1];
    }

    // Start of scan: everything from here on is entropy-coded image data.
    if (marker === 0xda) {
      keep.push([offset, bytes.length]);
      reachedScan = true;
      break;
    }
    // Standalone markers carry no length field: SOI, EOI, the restart markers
    // and TEM (0x01).
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
      keep.push([offset, offset + 2]);
      offset += 2;
      // eslint-disable-next-line no-continue
      continue;
    }

    if (offset + 4 > bytes.length) break;
    const length = (bytes[offset + 2] << 8) + bytes[offset + 3];
    // A segment length includes its own two bytes, so anything under 2 is
    // malformed.
    if (length < 2 || offset + 2 + length > bytes.length) break;
    const end = offset + 2 + length;
    const isApp = marker >= 0xe0 && marker <= 0xef;
    if (!isApp || !JPEG_DROP_MARKERS.has(marker)) keep.push([offset, end]);
    else if (
      marker === 0xe1 &&
      !orientation &&
      // "Exif", so an XMP APP1 is not read as one.
      bytes[offset + 4] === 0x45 &&
      bytes[offset + 5] === 0x78 &&
      bytes[offset + 6] === 0x69 &&
      bytes[offset + 7] === 0x66
    )
      orientation = readOrientation(bytes.subarray(offset, end));
    offset = end;
  }

  if (!reachedScan) return null; // fail open: upload the original untouched

  // 1 is "already upright" and is what a missing tag means anyway, so only a
  // real rotation is worth writing back.
  const restore = orientation > 1 ? orientationSegment(orientation) : null;
  const size =
    keep.reduce((total, [start, end]) => total + (end - start), 0) +
    (restore ? restore.length : 0);
  if (size === bytes.length && !restore) return null; // nothing was removed
  const out = new Uint8Array(size);
  let cursor = 0;
  keep.forEach(([start, end], index) => {
    out.set(bytes.subarray(start, end), cursor);
    cursor += end - start;
    // Straight after SOI, which is where a decoder expects to find APP1.
    if (index === 0 && restore) {
      out.set(restore, cursor);
      cursor += restore.length;
    }
  });
  return out;
};

// PNG chunks that can carry location or identifying text.
const PNG_DROP_CHUNKS = new Set(['eXIf', 'tEXt', 'iTXt', 'zTXt', 'tIME']);
const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

const stripPng = buffer => {
  const bytes = new Uint8Array(buffer);
  if (PNG_SIGNATURE.some((byte, index) => bytes[index] !== byte)) return null;

  const keep = [[0, 8]];
  let offset = 8;
  const view = new DataView(buffer);
  // Same rule as the JPEG path: a partial parse must not produce a partial
  // file. Only a run that reaches IEND is safe to emit.
  let reachedEnd = false;

  while (offset + 8 <= bytes.length) {
    const length = view.getUint32(offset);
    const type = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7],
    );
    // 4 length + 4 type + data + 4 CRC
    const end = offset + 12 + length;
    if (end > bytes.length) break;
    if (!PNG_DROP_CHUNKS.has(type)) keep.push([offset, end]);
    offset = end;
    if (type === 'IEND') {
      reachedEnd = true;
      break;
    }
  }

  if (!reachedEnd) return null; // fail open

  const size = keep.reduce((total, [start, end]) => total + (end - start), 0);
  if (size === bytes.length) return null;
  const out = new Uint8Array(size);
  let cursor = 0;
  keep.forEach(([start, end]) => {
    out.set(bytes.subarray(start, end), cursor);
    cursor += end - start;
  });
  return out;
};

/**
 * Returns a metadata-free copy of the file, or the file itself when there is
 * nothing to strip or the format is not one we can rewrite safely.
 *
 * Fails open on purpose: an upload that cannot be cleaned still goes through,
 * because refusing it would lose the author's photo over a format we did not
 * anticipate. The formats that actually carry GPS — JPEG and PNG — are both
 * handled.
 */
const stripImageMetadata = async file => {
  try {
    if (!file || !file.type) return file;
    const isJpeg = /^image\/jpe?g$/i.test(file.type);
    const isPng = /^image\/png$/i.test(file.type);
    if (!isJpeg && !isPng) return file;

    const buffer = await file.arrayBuffer();
    const stripped = isJpeg ? stripJpeg(buffer) : stripPng(buffer);
    if (!stripped) return file;

    const clean = new File([stripped], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
    return clean;
  } catch {
    return file;
  }
};

export { stripJpeg, stripPng };
export default stripImageMetadata;
